import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { Events, Properties, Referrers } from '../../../enums/Analytics';
import { formatUrl } from '../../../lib/String';
import { getOrderedSalesActionsFromStructure } from '../../../lib/helper/RoomStructureHelper';
import { isVanityUrl } from '../../../lib/helper/RoomHelper';
import {
  RoomJSONStructureSchema,
  SalesActionSchema,
} from '../../../definitions/schemas';
import { InteractionEvent } from '../../../enums/InteractionEvent';
import { SimplifiedTrackingProps } from '../../Rooms/Tracking/types';
import Analytics, { EventProperty } from '../../../tools/Analytics';
import ThemableButton from '../../../theme/components/Button/ThemableButton';
import TrackInteractionInterface from '../../../graphql/Rooms/TrackInteractionInterface';

/**
 * Exclude from tracking sales action clicked:
 *   - Preview on create/edit room
 *   - Preview on create/edit room template
 *   - Preview on room templates table
 */
const REFERRERS_EXCLUDED_FROM_TRACKING = [
  `${Referrers.DEAL_ROOMS.HOME}-${Referrers.DEAL_ROOMS.EDIT} - Preview`,
  `${Referrers.DEAL_ROOMS.HOME}-${Referrers.DEAL_ROOMS.CREATE} - Preview`,
  `${Referrers.ROOM_TEMPLATES.HOME}-${Referrers.ROOM_TEMPLATES.EDIT} - Preview`,
  `${Referrers.ROOM_TEMPLATES.HOME}-${Referrers.ROOM_TEMPLATES.CREATE} - Preview`,
  `${Referrers.ROOM_TEMPLATES.HOME} - Preview`,
];

export interface RoomSalesActionsProps {
  actionable?: boolean;
  referrer: string;
  roomId: string;
  salesActions: SalesActionSchema[] | undefined;
  structure: RoomJSONStructureSchema;
  trackingProperties: EventProperty;
  TrackingProps?: SimplifiedTrackingProps;
}

const RoomSalesActions: React.FC<RoomSalesActionsProps> = (
  props: RoomSalesActionsProps
): JSX.Element => {
  const {
    actionable,
    referrer,
    roomId,
    salesActions = [],
    structure,
    trackingProperties,
    TrackingProps,
  } = props;

  // The structure can hold deleted buttons (rooms created before it was deleted)
  // then orderedSalesActions will only match active/valid buttons
  const orderedSalesActions = useMemo(
    () => getOrderedSalesActionsFromStructure(salesActions, structure),
    [salesActions, structure]
  );

  if (orderedSalesActions.length === 0) {
    return <></>;
  }

  // Do not set the primary button from the structure
  // because it can hold deleted buttons
  const primaryButton = orderedSalesActions[0];

  const trackInteraction = async (salesAction: SalesActionSchema) => {
    if (
      !TrackingProps?.track ||
      REFERRERS_EXCLUDED_FROM_TRACKING.includes(referrer)
    ) {
      return;
    }

    try {
      new TrackInteractionInterface(TrackingProps?.contactId).trackInteraction({
        event: InteractionEvent.SALES_ACTION_CLICKED,
        measure: 1,
        metadata: {
          id: salesAction.id,
          label: salesAction.label,
          url: salesAction.url,
        },
        roomId,
      });
    } catch (e) {
      // do nothing
    }
  };

  const trackAnalytics = (salesActions: SalesActionSchema) => {
    if (REFERRERS_EXCLUDED_FROM_TRACKING.includes(referrer)) {
      return;
    }

    Analytics.track(Events.SALES_ACTION_CLICKED, {
      ...trackingProperties,
      [Properties.VANITY_URL]: isVanityUrl(window.location.href),
      [Properties.SALES_ACTION]: salesActions.label,
    });
  };

  const trackSalesAction = (salesAction: SalesActionSchema) => {
    trackAnalytics(salesAction);
    trackInteraction(salesAction);
  };

  return (
    <Box>
      {orderedSalesActions.map((salesAction: SalesActionSchema) => {
        const isPrimary = primaryButton && primaryButton.id === salesAction.id;

        return (
          <Box key={salesAction.id} mb={2}>
            <ThemableButton
              disabled={!actionable}
              fullWidth
              href={formatUrl(salesAction.url)}
              onClick={() => trackSalesAction(salesAction)}
              style={{ borderWidth: 2 }}
              target={
                salesAction.url.startsWith('mailto:') ? undefined : '_blank'
              }
              variant={isPrimary ? 'contained' : 'outlined'}
            >
              {salesAction.label}
            </ThemableButton>
          </Box>
        );
      })}
    </Box>
  );
};

export default RoomSalesActions;
